import { booleanAttribute } from "#ce/utils/props";

// `value` is absent on purpose and is documented in the story: the element owns a live `value`
// property, which a declared prop would shadow with an accessor onto the prop store. The `value`
// attribute seeds the field and names what a form reset restores.
export const props = Object.freeze({
  /**
   * Label rendered above the field.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Placeholder shown while no date is selected.
   *
   * @type {String}
   * @default undefined
   */
  placeholder: {
    type: String,
    default: undefined,
  },
  /**
   * Helper text under the field. Shown while the field is focused, or always when `persistentHint` is set.
   *
   * @type {String}
   * @default undefined
   */
  hint: {
    type: String,
    default: undefined,
  },
  /**
   * Keep the hint visible instead of showing it only on focus.
   *
   * @type {Boolean|String}
   * @default false
   */
  persistentHint: booleanAttribute(),
  /**
   * Messages shown under the field, for server-side errors the browser's own validation cannot produce.
   *
   * @type {String}
   * @default undefined
   */
  messages: {
    type: String,
    default: undefined,
  },
  /**
   * Marks the field invalid with this message, for a rule the browser cannot check. An empty string clears it.
   *
   * @type {String}
   * @default undefined
   */
  customValidity: {
    type: String,
    default: undefined,
  },
  /**
   * Field density, forwarded to `sp-text-field`.
   *
   * @type {String}
   * @default undefined
   */
  density: {
    type: String,
    default: undefined,
  },
  /**
   * Field variant, forwarded to `sp-text-field`.
   *
   * @type {String}
   * @default undefined
   */
  variant: {
    type: String,
    default: undefined,
  },
  /**
   * Let the user type a date instead of only picking one. Typed text is parsed against `displayFormat` on blur; text that does not parse is discarded and the field falls back to the current selection.
   *
   * @type {Boolean|String}
   * @default false
   */
  allowInput: booleanAttribute(),
  /**
   * Show a clear button once a date is selected. It appears on hover unless `persistentClear` keeps it visible.
   *
   * @type {Boolean|String}
   * @default false
   */
  clearable: booleanAttribute(),
  /**
   * Keep the clear button visible whenever a date is selected, rather than only while the field is hovered.
   *
   * @type {Boolean|String}
   * @default false
   */
  persistentClear: booleanAttribute(),
  /**
   * Add a time to the selection. The calendar grows a time footer, and both the value and the field text carry the time.
   *
   * @type {Boolean|String}
   * @default false
   */
  enableTime: booleanAttribute(),
  /**
   * Select a start and an end date rather than one date. The value and the field text carry both, joined by ` to `, and the calendar reports the pair only once the second end is picked. A range is picked, never typed, so `allowInput` has no effect here.
   *
   * @type {Boolean|String}
   * @default false
   */
  range: booleanAttribute(),
  /**
   * Format of the text in the field, in flatpickr tokens — `F j, Y h:i K` renders as `March 15, 2026 9:30 AM`. Left unset it is `m/d/Y`, extended to `m/d/Y h:i K` when `enableTime` is set, so the chosen time is legible and typed text carrying a time still parses.
   *
   * @type {String}
   * @default "m/d/Y"
   */
  displayFormat: {
    type: String,
    default: undefined,
  },
  /**
   * Format of the value the field submits, in flatpickr tokens. Left unset, flatpickr uses `Y-m-d`, extended to `Y-m-d H:i` when `enableTime` is set.
   *
   * @type {String}
   * @default undefined
   */
  valueFormat: {
    type: String,
    default: undefined,
  },
  /**
   * Disable the field — the calendar cannot be opened and no date can be selected.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Require a date before the form can be submitted.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Message the browser shows when a required field is submitted without a date.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on the visible input. Pass a catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on every day cell in the calendar. Pass a catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  dayTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on the calendar's previous-month control. Pass a catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  prevMonthTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on the calendar's next-month control. Pass a catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  nextMonthTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Open the calendar popup. The field opens and closes itself as the user works, and reports each transition as `update-open`, so a caller driving this keeps its own copy in step with that event.
   *
   * @type {Boolean|String}
   * @default false
   */
  open: booleanAttribute(),
  /**
   * Close the popup on any click inside it, rather than only when a selection is complete. A range takes a second click and a time is set inside the popup as well, so leave this off unless the popup holds nothing beyond a single date.
   *
   * @type {Boolean|String}
   * @default false
   */
  closeOnContentClick: booleanAttribute(),
});
